import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { withGuard } from 'ui-components/components/with-guard';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import messages from './messages';
import style from './style.module.less';
import ModalPortal from '../../../../../components/modal-portal';
import { cancelVendorInvite } from '../../../api/vendor-api';
import { PendingProviderPropType } from '../../types';

const CancelVendorModal = ({ onClose, provider }) => {
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const [didError, setDidError] = useAsyncSafeState(false);
  const name = provider?.partnerName ?? '';

  const saveSettings = useCallback(async () => {
    setIsSaving(true);
    setDidError(false);
    try {
      await cancelVendorInvite(provider?.externalInviteId);
      onClose();
      setIsSaving(false);
    } catch {
      setIsSaving(false);
      setDidError(true);
    }
  }, [setIsSaving, setDidError, onClose, provider?.externalInviteId]);

  const onCloseErrorBanner = useCallback(async () => setDidError(false), [setDidError]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='small'>
        <ModalHeader>
          <FormattedMessage {...messages.cancelEmailHeader} values={{ vendorName: name }} />
        </ModalHeader>
        {didError ? (
          <BannerMessageV2
            content={<FormattedMessage {...messages.errorBannerText} />}
            onClose={onCloseErrorBanner}
            status={BannerStatus.Error}
          />
        ) : null}
        <ModalBody className={style['ss-settings-modal-body']}>
          <div className={style['modal-body-text']}>
            <FormattedMessage {...messages.cancelInviteModalBodyText} values={{ vendorName: name }} />
          </div>
        </ModalBody>
        <ModalFooter className={style['ss-settings-modal-footer']}>
          <Button disabled={isSaving} onClick={onClose}>
            <FormattedMessage {...messages.keepInviteButton} />
          </Button>
          <Button disabled={isSaving ?? !name.length} isLoading={isSaving} onClick={saveSettings} theme='danger'>
            <FormattedMessage {...messages.cancelInviteButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

CancelVendorModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  provider: PendingProviderPropType.isRequired,
};
const ExtendedModal = withGuard(CancelVendorModal);
export { ExtendedModal as CancelVendorModal };
